import React from 'react';
import { useTranslation } from "react-i18next";
import { makeStyles } from 'tss-react/mui';
import NavDetails from "components/Elements/NavDetails";
import { analyticsPath, appPath, experimentalPath, newAnalyticsPath } from "helpers/routePaths";
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import MonitorIcon from '@mui/icons-material/Monitor';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CategoryIcon from '@mui/icons-material/Category';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import TimerIcon from '@mui/icons-material/Timer';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HistoryIcon from '@mui/icons-material/History';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link, Routes, Route } from 'react-router-dom';
import { Typography } from '@mui/material';
import {
  CompletedVsOutstandingReport,
  DefectsTypeReport,
  DefectsRiskRatingReport,
  DefectsSystemReport,
  DefectsRegisteredReport,
  DefectsDepartmentReport,
  ServicePauseReport,
  ServiceSkippedReport,
  ServiceStatusReport,
  ServiceStopReport,
  ServiceAverageTimesReport,
  ServiceTimesReport,
  ServiceTimesAnalytics,
  MissingPartsToolsReport,
  ConditionMonitoringReport,
  OverdueServicesReport,
  PlannedWorkComplianceReport,
  DefectsStatusReport,
  CompletedServicesAnalysis,
} from './Reports';
import AuditHistoryReport, { isSiteAdministrator } from './Reports/AuditHistory';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: '0',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 500,
    marginBottom: '8px',
    color: '#000',
  },
  sectionSubtitle: {
    fontSize: '20px',
    fontWeight: 500,
    marginTop: '32px',
    marginBottom: '8px',
    color: '#000',
  },
  sectionDescription: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '14px',
    marginBottom: '24px',
  },
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  analyticsLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    padding: '20px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      '& $title': {
        color: 'rgb(48,122,215)',
      }
    }
  },
  title: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  description: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '14px',
    marginLeft: '28px',
  },
  icon: {
    color: 'rgb(48,122,215)',
    fontSize: '20px',
  }
}));

const AnalyticsDashboard = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const me = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const { customCaptions } = me;
  const showAuditHistory = isSiteAdministrator(me);

  const defectAnalysisLinks = [
    {
      title: t("title.entityRegistered", { entity: customCaptions?.defectPlural }),
      description: t("message.trackNewlyRegisteredEntity", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <PlaylistAddCheckIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-registered`
    },
    {
      title: t("title.entityByType", { entity: customCaptions?.defectPlural, type: t("label.type") }),
      description: t("message.analyseEntityByClassification", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <BugReportIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-by-type`
    },
    {
      title: t("title.entityByDepartment", { entity: customCaptions?.defectPlural }),
      description: t("message.viewEntityAcrossDepartments", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <EngineeringIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-by-department`
    },
    {
      title: t("title.entityByRiskRating", { entity: customCaptions?.defectPlural, riskRating: customCaptions?.riskRating }),
      description: t("message.viewEntityByRiskLevel", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <PriorityHighIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-risk-rating`
    },
    {
      title: t("title.entityBySystem", { entity: customCaptions?.defectPlural, system: customCaptions?.system || 'System' }),
      description: t("message.monitorEntityAcrossSystems", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <CategoryIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-system`
    },
    {
      title: t("option.entityByStatus", { entity: customCaptions?.defectPlural }),
      description: t("message.viewEntityByStatus", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <DonutLargeIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/defects-by-status`
    },
    {
      title: t("title.completedVsOutstandingEntity", { entity: customCaptions?.defectPlural }),
      description: t("message.compareResolvedPendingEntity", { entity: customCaptions?.defectPlural?.toLowerCase() }),
      icon: <ShowChartIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/completed-vs-outstanding`
    },
  ];

  const serviceManagementLinks = [
    {
      title: t("title.entityAverageTimes", { entity: customCaptions?.service }),
      description: t("message.measureServiceDuration"),
      icon: <TimerIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-average-times`
    },
    {
      title: t("title.completedEntityTimesByWorkOrder", { entity: customCaptions?.service || 'Service' }),
      description: t("message.detailedServiceTimings"),
      icon: <HourglassTopIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-times`
    },
    {
      title: t("title.entityPauseReasons", { entity: customCaptions?.service }),
      description: t("message.analyseServiceInterruptions"),
      icon: <PauseCircleOutlineIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-pause`
    },
    {
      title: t("title.entityStopReasons", { entity: customCaptions?.service }),
      description: t("message.trackServiceStoppages"),
      icon: <StopCircleIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-stop`
    },
    {
      title: t("title.entityStatusChangeReasons", { entity: customCaptions?.service }),
      description: t("message.monitorServiceStatusTransitions"),
      icon: <ChangeCircleIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-status`
    },
    {
      title: t("title.missingEntityByReason", { entity: customCaptions?.toolPlural || 'Tools' }),
      description: t("message.trackInventoryToolAvailability"),
      icon: <BuildIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/missing-parts-tools`
    },
    {
      title: t("title.serviceSkippedTaskReasons", { service: customCaptions?.service, task: customCaptions?.task }),
      description: t("message.trackSkippedTasks"),
      icon: <SkipNextIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/service-skipped`
    },
    {
      title: t("title.completedEntityAnalysis", { entity: customCaptions?.servicePlural }),
      description: t("message.analyzeCompletedEntityTimes", { entity: customCaptions?.servicePlural?.toLowerCase() }),
      icon: <CheckCircleIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/completed-services-analysis`
    },
  ];

  const performanceMetricsLinks = [
    {
      title: t("title.conditionMonitoring"),
      description: t("message.monitorSystemHealth"),
      icon: <MonitorIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/condition-monitoring`
    },
    {
      title: t("title.overdueEntity", { entity: customCaptions?.servicePlural }),
      description: t("message.trackOverdueEntity", { entity: customCaptions?.servicePlural?.toLowerCase() }),
      icon: <HistoryIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/overdue-services`
    },
    {
      title: t("title.plannedWorkCompliance"),
      description: t("message.trackPlannedWorkCompliance"),
      icon: <PieChartIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/planned-work-compliance`
    },
  ];

  const auditHistoryLinks = [
    {
      title: t("title.auditHistory"),
      description: t("message.auditHistoryCardDescription", {
        services: customCaptions?.servicePlural?.toLowerCase(),
        defects: customCaptions?.defectPlural?.toLowerCase(),
      }),
      icon: <ManageSearchIcon className={classes.icon} />,
      path: `${appPath}${newAnalyticsPath}/audit-history`
    },
  ];

  const AnalyticsLink = ({ title, description, icon, path }) => (
    <Link to={path} className={classes.analyticsLink}>
      <Typography className={classes.title}>
        {icon}
        {title}
      </Typography>
      <Typography className={classes.description}>
        {description}
      </Typography>
    </Link>
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className={classes.container}>
            <NavDetails
              history={false}
              status={false}
              staticCrumbs={[]}
            />
            <Typography className={classes.sectionTitle}>
              {t("title.analyticsDashboard")}
            </Typography>
            <Typography className={classes.sectionDescription}>
              {t("message.dashboardDescription", { entity: customCaptions?.defectPlural?.toLowerCase() })}
            </Typography>

            <Typography className={classes.sectionSubtitle}>
              {t("title.entityAnalysis", { entity: customCaptions?.defect })}
            </Typography>
            <Typography className={classes.sectionDescription}>
              {t("message.trackAnalyseEntityDimensions", { entity: customCaptions?.defectPlural?.toLowerCase() })}
            </Typography>
            <div className={classes.analyticsGrid}>
              {defectAnalysisLinks.map((link, index) => (
                <AnalyticsLink key={index} {...link} />
              ))}
            </div>

            <Typography className={classes.sectionSubtitle}>
              {t("title.entityManagement", { entity: customCaptions?.service })}
            </Typography>
            <Typography className={classes.sectionDescription}>
              {t("message.monitorEntityOperations", { entity: customCaptions?.service?.toLowerCase() })}
            </Typography>
            <div className={classes.analyticsGrid}>
              {serviceManagementLinks.map((link, index) => (
                <AnalyticsLink key={index} {...link} />
              ))}
            </div>

            <Typography className={classes.sectionSubtitle}>
              {t("title.performanceMetrics")}
            </Typography>
            <Typography className={classes.sectionDescription}>
              {t("message.trackKpiTrends")}
            </Typography>
            <div className={classes.analyticsGrid}>
              {performanceMetricsLinks.map((link, index) => (
                <AnalyticsLink key={index} {...link} />
              ))}
            </div>

            {showAuditHistory && (
              <>
                <Typography className={classes.sectionSubtitle}>
                  {t("title.auditHistory")}
                </Typography>
                <Typography className={classes.sectionDescription}>
                  {t("message.auditHistoryDescription")}
                </Typography>
                <div className={classes.analyticsGrid}>
                  {auditHistoryLinks.map((link, index) => (
                    <AnalyticsLink key={index} {...link} />
                  ))}
                </div>
              </>
            )}
          </div>
        }
      />
      <Route 
        path="completed-vs-outstanding" 
        element={<CompletedVsOutstandingReport />} 
      />
      <Route path="defects-by-type" element={<DefectsTypeReport />} />
      <Route path="defects-by-department" element={<DefectsDepartmentReport />} />
      <Route path="defects-risk-rating" element={<DefectsRiskRatingReport />} />
      <Route path="defects-system" element={<DefectsSystemReport />} />
      <Route path="defects-registered" element={<DefectsRegisteredReport />} />
      <Route path="service-pause" element={<ServicePauseReport />} />
      <Route path="service-skipped" element={<ServiceSkippedReport />} />
      <Route path="service-status" element={<ServiceStatusReport />} />
      <Route path="service-stop" element={<ServiceStopReport />} />
      <Route path="service-average-times" element={<ServiceAverageTimesReport />} />
      <Route path="service-times" element={<ServiceTimesAnalytics />} />
      <Route path="missing-parts-tools" element={<MissingPartsToolsReport />} />
      <Route path="condition-monitoring" element={<ConditionMonitoringReport />} />
      <Route path="overdue-services" element={<OverdueServicesReport />} />
      <Route path="planned-work-compliance" element={<PlannedWorkComplianceReport />} />
      <Route path="defects-by-status" element={<DefectsStatusReport />} />
      <Route path="completed-services-analysis" element={<CompletedServicesAnalysis />} />
      <Route path="audit-history" element={<AuditHistoryReport />} />
    </Routes>
  );
};

export default AnalyticsDashboard;